import { useEffect, useRef, useState } from 'react';
import { clockNow } from '@/lib/clock';

/**
 * Prerendering the landing page has no visitor and no clock. A fixed reference keeps the built HTML
 * byte-identical between builds instead of baking in whatever time the build machine happened to be at;
 * the real time arrives on the first render in the browser.
 *
 * Local time, not UTC: the preview card formats with `date-fns` in the machine's zone, so an instant would
 * render as a different string on a laptop than in CI. This reads 09:41 wherever it is built.
 */
const PRERENDER_NOW = new Date(2026, 0, 1, 9, 41);

/**
 * A clock that re-renders only when `derive(now)` changes (e.g. the displayed second), and re-syncs immediately
 * when the tab becomes visible again so a backgrounded timer snaps to the right value.
 */
export function useNow(active: boolean, derive: (now: Date) => unknown): Date {
  const [now, setNow] = useState(() => (typeof window === 'undefined' ? PRERENDER_NOW : clockNow()));
  const last = useRef(derive(now));
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const n = clockNow();
      const d = derive(n);
      if (d !== last.current) { last.current = d; setNow(n); }
    };
    tick();
    const id = setInterval(tick, 200);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [active, derive]);
  return now;
}
